import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';

import {
    arrow,
    autoUpdate,
    flip,
    hide,
    offset,
    safePolygon,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useFloatingNodeId,
    useFocus,
    useHover,
    useInteractions,
    useRole,
    useTransitionStyles
} from '@floating-ui/react';

import useControlledState from '../../hooks/useControlledState';

import {
    FLOATING_DEFAULT_TRANSITION_DURATION,
    FLOATING_MINIMUM_REST_DELAY,
    FLOATING_PLACEMENT_MAP
} from './Floating.constants';
import { type FloatingOptions } from './Floating.types';
import {
    getTransformOrigin,
    parseOffset,
    stickyOnTopWhileScrolling as stickyOnTopWhileScrollingMiddleware
} from './Floating.utils';

export const useFloatingSetup = ({
    initialOpen = false,
    placement = 'top',
    open: controlledOpen,
    onOpenChange,
    trigger = 'hover',
    mouseEnterDelay,
    mouseLeaveDelay,
    mouseRestDelay,
    align,
    animationDuration = FLOATING_DEFAULT_TRANSITION_DURATION,
    role = 'dialog',
    stickyOnTopWhileScrolling = false
}: FloatingOptions = {}) => {
    const [open, setOpen] = useControlledState(
        controlledOpen,
        initialOpen,
        'useFloatingSetup',
        'open'
    );
    const arrowRef = useRef<SVGSVGElement>(null);
    const floatingPlacement = FLOATING_PLACEMENT_MAP[placement];
    const triggers = Array.isArray(trigger) ? trigger : [trigger];

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            setOpen(nextOpen);
            onOpenChange?.(nextOpen);
        },
        [setOpen, onOpenChange]
    );
    const nodeId = useFloatingNodeId();
    const data = useFloating({
        strategy: 'fixed',
        nodeId,
        placement: floatingPlacement,
        open,
        onOpenChange: handleOpenChange,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(parseOffset(align?.offset)),
            arrow({ element: arrowRef, padding: 4 }),
            hide({ strategy: 'referenceHidden' }),
            ...(stickyOnTopWhileScrolling
                ? [stickyOnTopWhileScrollingMiddleware(stickyOnTopWhileScrolling)]
                : [
                      flip({
                          crossAxis: floatingPlacement.includes('-'),
                          fallbackAxisSideDirection: 'start',
                          padding: 4
                      }),
                      shift({ padding: 4 })
                  ])
        ]
    });

    const { context, middlewareData } = data;

    const delay = {
        open: mouseEnterDelay,
        close: mouseLeaveDelay
    };

    const hover = useHover(context, {
        move: false,
        enabled: triggers.includes('hover'),
        delay,
        // Setting a minimum `restMs` avoids the animation to triggered immediately
        // when the mouse is over the trigger which would cause a flicker effect
        // (e.g. when the placement is right and the mouse moves from the left to the right).
        // It also avoids the user being annoyed by multiple tooltips popping up when moving the mouse around.
        restMs: Math.max(mouseEnterDelay ?? mouseRestDelay ?? 0, FLOATING_MINIMUM_REST_DELAY),
        handleClose: safePolygon()
    });
    const focus = useFocus(context, {
        enabled: triggers.includes('focus')
    });
    const click = useClick(context, {
        enabled: triggers.includes('click')
    });
    const dismiss = useDismiss(context);
    const roleData = useRole(context, { role });
    const interactions = useInteractions([hover, focus, click, dismiss, roleData]);

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
        duration: animationDuration,
        initial: {
            transform: 'scale(0.7)',
            opacity: 0
        },
        common: ({ side }) => ({
            transitionProperty: 'opacity, transform',
            transitionDuration: `${animationDuration}ms`,
            transformOrigin: getTransformOrigin(
                side,
                middlewareData.arrow?.x ?? 0,
                middlewareData.arrow?.y ?? 0
            ),
            willChange: 'opacity, transform'
        }),
        open: {
            opacity: 1,
            transform: 'scale(1)'
        },
        close: {
            opacity: 0,
            transform: 'scale(0.7)'
        }
    });

    const floatingStyles = useMemo(
        () => ({
            ...data.floatingStyles,
            ...((context.middlewareData.hide?.referenceHidden && {
                visibility: 'hidden',
                pointerEvents: 'none'
            }) as React.CSSProperties)
        }),
        [data.floatingStyles, context.middlewareData.hide?.referenceHidden]
    );

    return useMemo(
        () => ({
            ...data,
            open,
            setOpen,
            onOpenChange: handleOpenChange,
            arrowRef,
            trigger,
            mouseEnterDelay,
            mouseLeaveDelay,
            nodeId,
            transitionStyles,
            isMounted,
            floatingStyles,
            ...interactions
        }),
        [
            open,
            setOpen,
            interactions,
            data,
            trigger,
            mouseEnterDelay,
            mouseLeaveDelay,
            arrowRef,
            handleOpenChange,
            nodeId,
            transitionStyles,
            isMounted,
            floatingStyles
        ]
    );
};
